import { Box, Divider } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import { IEmbedGet } from '@opengov/embed-types';
import { LocaleText } from '../../locale';
import { ESummaryTableRequest, IEntityPdfExportConfig } from '@opengov/reporting-types';
import { IColumnNameFilters } from '../../types/MultiDatasetTypes';

import { ChartRefObject, getChartSvgFromRef } from '@opengov/reporting-charts';
import { styles } from './Container.styles';
import { ExportPdfEmbedTable } from './embedTable/EmbedTable';
import { ExportPdfControllers } from './exportPdfControllers';
import { ExportPdfTableFormattingControllers } from './exportPdfTableFormattingControllers';
import { ExportPdfFilter } from './filter';
import { ExportPdfFooter } from './footer/ExportPdfFooter';
import { ExportPdfFooterControllers } from './footer/ExportPdfFooterControllers';
import { ExportPdfHeader } from './header/ExportPdfHeader';
import { ExportPdfHeaderControllers } from './header/ExportPdfHeaderControllers';
import { footerBottomMargins } from './margins';
import { loadExportEmbed } from './loadExportEmbed';
import { ExportPdfPreviewModal } from './preview/PreviewModal';
import { ExportPdfReportLayout } from './reportLayout';
import { EShowPageSetting } from './types';
import { initForm } from './usePdfState/reducer';
import { usePdfState } from './usePdfState/usePdfState';

export enum EPageOrientation {
  portrait = 'portrait',
  landscape = 'landscape'
}

const CONFIG_FIELD_KEYS: ReadonlyArray<keyof IEntityPdfExportConfig> = [
  'pageOrientation',
  'marginSize',
  'fontFamily',
  'showHeader',
  'showPageSetting',
  'showPrimaryTitle',
  'showSecondaryTitle',
  'showSubtitle',
  'showNumberFormattingThousands',
  'showAppliedFilters',
  'showFooter',
  'showNote',
  'showPageNumber',
  'showExportTimeStamp',
  'summaryTableFontSize',
  'columnsPerPage',
  'showGridlines',
  'maxColumnWidth',
  'pageNumberAlignment',
  'noteAlignment',
  'timestampAlignment',
  'currencySymbolDisplay',
  'showUnderlines',
  'keepRowGroupsTogetherOnPageBreaks',
  'keepColumnGroupsTogetherOnPageBreaks'
];

export interface IReportPdfExport {
  contentHtml: string;
  footerHtml: string;
  headerHtml?: string;
  continuationHeaderHtml?: string;
  pageOrientation: EPageOrientation;
}

export interface IExportPdfContainerProps {
  reportId: string;
  saveViewId: string;
  entityTitle: string;
  reportTitle: string;
  currentSavedViewTitle: string;
  localeText: LocaleText;
  isPdfExportModalOpen: boolean;
  setIsPdfExportModalOpen: (isPdfExportModalOpen: boolean) => void;
  currentViewFilters: IColumnNameFilters;
  exportPdf(props: IReportPdfExport): Promise<string>;
  generatePdfPreview(props: IReportPdfExport): Promise<string>;
  generatePreviewOnFirstRender?: boolean;
  readyToExport: boolean;
  previewTimeout?: number;
  chartRef: React.RefObject<ChartRefObject>;
  /**
   * These three props are optional to preserve backward compatibility with consumers that do not
   * support entity-level settings (e.g. embed rendering, other report types). When omitted, the
   * modal opens with default settings and the "Save as default" button is not rendered.
   *
   * - `entityPdfExportConfig`: pre-populates form state from the saved entity config (null = no config yet).
   * - `onSaveDefaultConfig`: called when the user clicks "Save as default"; its presence controls whether
   *   the button is shown at all.
   * - `isEntityPdfConfigReady`: delays embed creation until the entity config fetch completes; defaults
   *   to `true` so existing consumers are unaffected.
   */
  entityPdfExportConfig?: IEntityPdfExportConfig | null;
  onSaveDefaultConfig?: (config: IEntityPdfExportConfig) => Promise<void>;
  isEntityPdfConfigReady?: boolean;
}

export const convertCurrentViewFiltersToText = (viewFilters: IColumnNameFilters | undefined): React.ReactElement[] => {
  if (viewFilters) {
    const filters = [];
    const filterKeys = Object.keys(viewFilters);

    for (let i = 0; i < filterKeys.length; i++) {
      const key = filterKeys[i];
      const filter = viewFilters[key];
      const filterText = (filter?.filters ?? [])
        .map((item) => `${item.operator} "${item.value}"`)
        .join(` ${filter?.booleanOperator} `);

      filters.push(
        <>
          <strong>{filter?.label}</strong> {`${filterText}${i !== filterKeys.length - 1 ? ',' : ''} `}
        </>
      );
    }
    return filters;
  }
  return [];
};

export const getTotalFilterCount = (viewFilters: IColumnNameFilters = {}): number => {
  return Object.keys(viewFilters).reduce((acc, key) => {
    return acc + (viewFilters[key]?.filters?.length ?? 0);
  }, 0);
};

export const ExportPdfContainer: React.FC<IExportPdfContainerProps> = ({
  reportId,
  saveViewId,
  entityTitle,
  reportTitle,
  currentSavedViewTitle,
  localeText,
  setIsPdfExportModalOpen,
  currentViewFilters,
  exportPdf,
  generatePdfPreview,
  generatePreviewOnFirstRender = true,
  readyToExport,
  previewTimeout = 2000,
  chartRef,
  entityPdfExportConfig,
  onSaveDefaultConfig,
  isEntityPdfConfigReady = true
}) => {
  const [
    {
      pageOrientation,
      marginSize,
      fontFamily,
      showHeader,
      showPageSetting,
      primaryTitle,
      secondaryTitle,
      subtitle,
      showPrimaryTitle,
      showSecondaryTitle,
      showSubtitle,
      showNumberFormattingThousands,
      showAppliedFilters,
      showPdfLoadingSpinner,
      previewCount,
      previewLink,
      showFooter,
      note,
      showNote,
      showPageNumber,
      showExportTimeStamp,
      summaryTableFontSize,
      columnsPerPage,
      showGridlines,
      maxColumnWidth,
      debounceMaxColumnWidth,
      pageNumberAlignment,
      noteAlignment,
      timestampAlignment,
      currencySymbolDisplay,
      showUnderlines,
      keepRowGroupsTogetherOnPageBreaks,
      keepColumnGroupsTogetherOnPageBreaks
    },
    actions
  ] = usePdfState(entityTitle, reportTitle, currentSavedViewTitle, currentViewFilters, entityPdfExportConfig);
  const firstRender = useRef<boolean>(true);
  const previewTimerId = useRef<number>(0);
  const entityConfigSyncedRef = useRef(false);
  const [embed, setEmbed] = useState<IEmbedGet | null>(null);
  const [embedLoadFailed, setEmbedLoadFailed] = useState(false);
  const [previewLoadFailed, setPreviewLoadFailed] = useState(false);
  const [lastSavedConfig, setLastSavedConfig] = useState<IEntityPdfExportConfig | null | undefined>(
    entityPdfExportConfig
  );
  const showHeaderInAllPages = showPageSetting === EShowPageSetting.onAllPages;

  // When entityPdfExportConfig arrives asynchronously (after the modal opens), sync it into
  // lastSavedConfig exactly once so hasConfigChanged reflects the true server-side baseline.
  useEffect(() => {
    if (entityPdfExportConfig && !entityConfigSyncedRef.current) {
      entityConfigSyncedRef.current = true;
      setLastSavedConfig(entityPdfExportConfig);
    }
  }, [entityPdfExportConfig]);

  const hasConfigChanged = useMemo(() => {
    const currentConfig: IEntityPdfExportConfig = {
      pageOrientation,
      marginSize,
      fontFamily,
      showHeader,
      showPageSetting,
      showPrimaryTitle,
      showSecondaryTitle,
      showSubtitle,
      showNumberFormattingThousands,
      showAppliedFilters,
      showFooter,
      showNote,
      showPageNumber,
      showExportTimeStamp,
      summaryTableFontSize,
      columnsPerPage,
      showGridlines,
      maxColumnWidth,
      pageNumberAlignment,
      noteAlignment,
      timestampAlignment,
      currencySymbolDisplay,
      showUnderlines,
      keepRowGroupsTogetherOnPageBreaks,
      keepColumnGroupsTogetherOnPageBreaks
    };
    const savedConfig: IEntityPdfExportConfig = { ...initForm, ...(lastSavedConfig ?? {}) };
    return CONFIG_FIELD_KEYS.some((key) => currentConfig[key] !== savedConfig[key]);
  }, [
    pageOrientation,
    marginSize,
    fontFamily,
    showHeader,
    showPageSetting,
    showPrimaryTitle,
    showSecondaryTitle,
    showSubtitle,
    showNumberFormattingThousands,
    showAppliedFilters,
    showFooter,
    showNote,
    showPageNumber,
    showExportTimeStamp,
    summaryTableFontSize,
    columnsPerPage,
    showGridlines,
    maxColumnWidth,
    pageNumberAlignment,
    noteAlignment,
    timestampAlignment,
    currencySymbolDisplay,
    showUnderlines,
    lastSavedConfig,
    keepRowGroupsTogetherOnPageBreaks,
    keepColumnGroupsTogetherOnPageBreaks
  ]);

  // Increases the page bottom margin when multiple footer items share the same alignment column,
  // preventing the taller footer from overlapping with table content.
  const footerBottomMargin = useMemo((): string | undefined => {
    if (!showFooter) return undefined;
    const activeAlignments = [
      showPageNumber && pageNumberAlignment,
      showExportTimeStamp && timestampAlignment,
      showNote && noteAlignment
    ].filter(Boolean);
    const uniqueCount = new Set(activeAlignments).size;
    if (uniqueCount === activeAlignments.length) return undefined;
    if (uniqueCount === 1 && activeAlignments.length === 3) return footerBottomMargins.threeItemsStacked;
    return footerBottomMargins.twoItemsStacked;
  }, [
    showFooter,
    showPageNumber,
    showExportTimeStamp,
    showNote,
    pageNumberAlignment,
    timestampAlignment,
    noteAlignment
  ]);

  const reportChartSvg = useMemo(() => {
    if (pageOrientation === EPageOrientation.landscape) {
      return getChartSvgFromRef(chartRef, { width: 900 });
    }
    return getChartSvgFromRef(chartRef, { width: 700 });
  }, [pageOrientation, chartRef]);

  useEffect(() => {
    if (!isEntityPdfConfigReady) return;
    // The embed runs the full report query, which large reports push past the CDN timeout, so it is built through
    // the async route and polled. Closing the modal stops the polling.
    let cancelled = false;
    setEmbedLoadFailed(false);
    loadExportEmbed(
      {
        title: '',
        report_id: reportId,
        saved_view_id: saveViewId,
        report_version: 'v2',
        should_sync: false,
        type: ESummaryTableRequest.summaryTableType
      },
      { isCancelled: () => cancelled }
    )
      .then((loadedEmbed) => {
        if (!cancelled) setEmbed(loadedEmbed);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error(error);
        setEmbedLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isEntityPdfConfigReady]);

  const exportPdfFilter = (
    <ExportPdfFilter
      marginSize={marginSize}
      filters={convertCurrentViewFiltersToText(currentViewFilters)}
      filterLabel={localeText.exportPdfModal.filterSection.filterLabel}
      displayFilter={showAppliedFilters}
      displayHeader={showHeader}
      fontFamily={fontFamily}
    />
  );

  const exportPdfHeader = (
    <ExportPdfHeader
      primaryTitle={primaryTitle}
      marginSize={marginSize}
      secondaryTitle={secondaryTitle}
      subtitle={subtitle}
      showPrimaryTitle={showPrimaryTitle}
      showSecondaryTitle={showSecondaryTitle}
      showSubtitle={showSubtitle}
      showNumberFormattingThousands={showNumberFormattingThousands}
      showHeader={showHeader}
      numberFormattingThousandsLabel={localeText.exportPdfModal.headerSection.numberFormattingThousands}
      continuationLabel={localeText.exportPdfModal.headerSection.continuationLabel}
      showHeaderInAllPages={showHeaderInAllPages}
      fontFamily={fontFamily}
    />
  );

  const continuationPdfHeader = (
    <ExportPdfHeader
      primaryTitle={primaryTitle}
      marginSize={marginSize}
      secondaryTitle={secondaryTitle}
      subtitle={subtitle}
      showPrimaryTitle={showPrimaryTitle}
      showSecondaryTitle={showSecondaryTitle}
      showSubtitle={showSubtitle}
      showNumberFormattingThousands={showNumberFormattingThousands}
      showHeader={showHeader}
      numberFormattingThousandsLabel={localeText.exportPdfModal.headerSection.numberFormattingThousands}
      continuationLabel={localeText.exportPdfModal.headerSection.continuationLabel}
      showHeaderInAllPages={showHeaderInAllPages}
      fontFamily={fontFamily}
      isContinuation={true}
    />
  );

  const exportPdfFooter = (
    <ExportPdfFooter
      marginSize={marginSize}
      displayTimestamp={showExportTimeStamp}
      displayPageNumber={showPageNumber}
      dateTime={DateTime.local()}
      note={note}
      displayNote={showNote}
      displayFooter={showFooter}
      fontFamily={fontFamily}
      pageNumberAlignment={pageNumberAlignment}
      noteAlignment={noteAlignment}
      timestampAlignment={timestampAlignment}
    />
  );

  const exportPdfLayout = (
    <ExportPdfReportLayout
      reportText={localeText}
      pageOrientation={pageOrientation}
      marginSize={marginSize}
      fontFamily={fontFamily}
      headerSection={showHeaderInAllPages ? null : exportPdfHeader}
      embedTable={
        <ExportPdfEmbedTable
          embed={embed}
          columnsPerPage={columnsPerPage}
          nameWidth={maxColumnWidth}
          fontSize={summaryTableFontSize}
          showGridlines={showGridlines}
          showUnderlines={showUnderlines}
          currencySymbolDisplay={currencySymbolDisplay}
          keepRowGroupsTogetherOnPageBreaks={keepRowGroupsTogetherOnPageBreaks}
          keepColumnGroupsTogetherOnPageBreaks={keepColumnGroupsTogetherOnPageBreaks}
        />
      }
      filterSection={exportPdfFilter}
      showHeaderInAllPages={showHeaderInAllPages}
      reportChartSvg={reportChartSvg}
      footerBottomMargin={footerBottomMargin}
    />
  );

  const generatePreview = async (timerId: number) => {
    try {
      if (embed) {
        setPreviewLoadFailed(false);
        const pdfSrc = await generatePdfPreview({
          contentHtml: ReactDOMServer.renderToString(exportPdfLayout),
          footerHtml: ReactDOMServer.renderToString(exportPdfFooter),
          headerHtml: showHeaderInAllPages ? ReactDOMServer.renderToString(exportPdfHeader) : undefined,
          continuationHeaderHtml:
            showHeader && showHeaderInAllPages ? ReactDOMServer.renderToString(continuationPdfHeader) : undefined,
          pageOrientation
        });

        if (timerId === previewTimerId.current) {
          actions.setShowPdfLoadingSpinner(false);
          actions.setPreviewLink(pdfSrc);
          actions.setPreviewCount(previewCount + 1);
        }
      }
    } catch (error) {
      actions.setShowPdfLoadingSpinner(false);
      setPreviewLoadFailed(true);
      console.error(error);
    }
  };

  const handleExport = async () => {
    await exportPdf({
      contentHtml: ReactDOMServer.renderToString(exportPdfLayout),
      footerHtml: ReactDOMServer.renderToString(exportPdfFooter),
      headerHtml: showHeaderInAllPages ? ReactDOMServer.renderToString(exportPdfHeader) : undefined,
      continuationHeaderHtml:
        showHeader && showHeaderInAllPages ? ReactDOMServer.renderToString(continuationPdfHeader) : undefined,
      pageOrientation
    });
  };

  useEffect(() => {
    if (generatePreviewOnFirstRender && firstRender.current) {
      actions.setShowPdfLoadingSpinner(true);
      firstRender.current = false;
      void generatePreview(0);
      return;
    }

    if (readyToExport) {
      actions.setShowPdfLoadingSpinner(true);
      // Generates a new preview after a 2 sec timeout to allow time for other selections to be made
      previewTimerId.current = setTimeout(
        () => generatePreview(previewTimerId.current),
        previewTimeout
      ) as unknown as number;
    }
    return () => {
      clearTimeout(previewTimerId.current);
    };
  }, [
    showHeader,
    showPageSetting,
    showPrimaryTitle,
    showSecondaryTitle,
    showSubtitle,
    showNumberFormattingThousands,
    primaryTitle,
    secondaryTitle,
    subtitle,
    fontFamily,
    pageOrientation,
    marginSize,
    showFooter,
    note,
    showNote,
    showPageNumber,
    showExportTimeStamp,
    embed,
    showAppliedFilters,
    summaryTableFontSize,
    columnsPerPage,
    showGridlines,
    debounceMaxColumnWidth,
    pageNumberAlignment,
    noteAlignment,
    timestampAlignment,
    currencySymbolDisplay,
    showUnderlines,
    keepRowGroupsTogetherOnPageBreaks,
    keepColumnGroupsTogetherOnPageBreaks
  ]);

  const controllers = (
    <Box>
      <ExportPdfControllers
        localeText={localeText}
        pageOrientation={pageOrientation}
        setPageOrientation={actions.setPageOrientation}
        marginSize={marginSize}
        setMarginSize={actions.setMarginSize}
        fontFamily={fontFamily}
        setFontFamily={(fontFamily) => {
          actions.setFontFamily(fontFamily);
        }}
      />
      <Divider sx={styles.horizontalDivider} />
      <ExportPdfHeaderControllers
        localeText={localeText}
        showHeader={showHeader}
        toggleShowHeader={actions.toggleShowHeader}
        showPageSetting={showPageSetting}
        setShowPageSetting={actions.setShowPageSetting}
        primaryTitle={primaryTitle}
        setPrimaryTitle={actions.setPrimaryTitle}
        secondaryTitle={secondaryTitle}
        setSecondaryTitle={actions.setSecondaryTitle}
        subtitle={subtitle}
        setSubtitle={actions.setSubtitle}
        showPrimaryTitle={showPrimaryTitle}
        toggleShowPrimaryTitle={actions.toggleShowPrimaryTitle}
        showSecondaryTitle={showSecondaryTitle}
        toggleShowSecondaryTitle={actions.toggleShowSecondaryTitle}
        showSubtitle={showSubtitle}
        toggleShowSubtitle={actions.toggleShowSubtitle}
        showNumberFormattingThousands={showNumberFormattingThousands}
        toggleShowNumberFormattingThousands={actions.toggleShowNumberFormattingThousands}
        showAppliedFilters={showAppliedFilters}
        toggleShowAppliedFilters={actions.toggleShowAppliedFilters}
        appliedFilters={getTotalFilterCount(currentViewFilters)}
        currencySymbolDisplay={currencySymbolDisplay}
        setCurrencySymbolDisplay={(display) => {
          actions.setCurrencySymbolDisplay(display);
        }}
      />
      <Divider sx={styles.horizontalDivider} />
      <ExportPdfFooterControllers
        localeText={localeText}
        toggleShowFooter={actions.toggleShowFooter}
        setNote={actions.setNote}
        toggleShowNote={actions.toggleShowNote}
        toggleShowPageNumber={actions.toggleShowPageNumber}
        toggleShowExportTimeStamp={actions.toggleShowExportTimeStamp}
        showFooter={showFooter}
        note={note}
        showNote={showNote}
        showPageNumber={showPageNumber}
        showExportTimeStamp={showExportTimeStamp}
        pageNumberAlignment={pageNumberAlignment}
        setPageNumberAlignment={(alignment) => {
          actions.setPageNumberAlignment(alignment);
        }}
        noteAlignment={noteAlignment}
        setNoteAlignment={(alignment) => {
          actions.setNoteAlignment(alignment);
        }}
        timestampAlignment={timestampAlignment}
        setTimestampAlignment={(alignment) => {
          actions.setTimestampAlignment(alignment);
        }}
      />
      <Divider sx={styles.horizontalDivider} />
      <ExportPdfTableFormattingControllers
        localeText={localeText}
        setShowGridlines={actions.setShowGridlines}
        setShowUnderlines={() => {
          actions.setShowUnderlines();
        }}
        setColumnsPerPage={actions.setColumnsPerPage}
        setMaxColumnWidth={actions.setMaxColumnWidth}
        setSummaryTableFontSize={actions.setSummaryTableFontSize}
        summaryTableFontSize={summaryTableFontSize}
        columnsPerPage={columnsPerPage}
        showGridlines={showGridlines}
        showUnderlines={showUnderlines}
        maxColumnWidth={maxColumnWidth}
        keepRowGroupsTogetherOnPageBreaks={keepRowGroupsTogetherOnPageBreaks}
        toggleKeepRowGroupsTogetherOnPageBreaks={() => {
          actions.toggleKeepRowGroupsTogetherOnPageBreaks();
        }}
        keepColumnGroupsTogetherOnPageBreaks={keepColumnGroupsTogetherOnPageBreaks}
        toggleKeepColumnGroupsTogetherOnPageBreaks={() => {
          actions.toggleKeepColumnGroupsTogetherOnPageBreaks();
        }}
      />
    </Box>
  );

  const getPreviewUnavailableMessage = (): string | undefined => {
    if (embedLoadFailed) return localeText.exportPdfModal.exportDataLoadError;
    if (previewLoadFailed) return localeText.exportPdfModal.previewError;
    return undefined;
  };

  return (
    <ExportPdfPreviewModal
      onClose={() => {
        setIsPdfExportModalOpen(false);
      }}
      localeText={localeText}
      options={controllers}
      handleExport={handleExport}
      previewLink={previewLink}
      previewCount={previewCount}
      showPdfLoadingSpinner={showPdfLoadingSpinner || !embed}
      isExportDisabled={previewLoadFailed}
      unavailableMessage={getPreviewUnavailableMessage()}
      onSaveDefaultConfig={
        onSaveDefaultConfig
          ? async () => {
              const currentConfig: IEntityPdfExportConfig = {
                pageOrientation,
                marginSize,
                fontFamily,
                showHeader,
                showPageSetting,
                showPrimaryTitle,
                showSecondaryTitle,
                showSubtitle,
                showNumberFormattingThousands,
                showAppliedFilters,
                showFooter,
                showNote,
                showPageNumber,
                showExportTimeStamp,
                summaryTableFontSize,
                columnsPerPage,
                showGridlines,
                maxColumnWidth,
                pageNumberAlignment,
                noteAlignment,
                timestampAlignment,
                currencySymbolDisplay,
                showUnderlines,
                keepRowGroupsTogetherOnPageBreaks,
                keepColumnGroupsTogetherOnPageBreaks
              };
              await onSaveDefaultConfig(currentConfig);
              setLastSavedConfig(currentConfig);
            }
          : undefined
      }
      isSetAsDefaultDisabled={!hasConfigChanged}
    />
  );
};
